import { ref, watch, unref, getCurrentScope, onScopeDispose } from 'vue'

/** Lo que tiene que tardar una carga para que merezca esqueleto. */
export const SKELETON_DELAY_MS = 150
/** Lo que se queda un esqueleto, como poco, una vez enseñado. */
export const SKELETON_MINIMUM_MS = 300

/**
 * Cuándo se enseña un esqueleto: el antiparpadeo.
 *
 * Una carga que tarda 60 ms y pinta un esqueleto durante esos 60 ms no se ve
 * como «cargando», se ve como un parpadeo: la pantalla destella gris y luego
 * pone los datos. Las dos reglas que lo evitan, y que hacen las apps nativas:
 *
 *   1. ESPERAR antes de enseñarlo (`delay`, 150 ms). Lo que llega antes no
 *      enseña esqueleto ninguno: pasa de nada a los datos.
 *   2. Una vez enseñado, DEJARLO un mínimo (`minimum`, 300 ms). Si los datos
 *      llegan 20 ms después de que apareciera, el esqueleto no destella: se
 *      queda lo justo para leerse como un estado y no como un fallo.
 *
 *   const loading = ref(true)
 *   const { visible } = useDelayedLoading(loading)
 *   // <UiSkeletonList v-if="visible" />
 *
 * Lo normal es no llamarlo a mano: `UiLoadState` lo usa por dentro.
 *
 * @param {import('vue').Ref<boolean>|(() => boolean)} source  si se está cargando
 * @param {{ delay?: number, minimum?: number }} [options]
 * @returns {{ visible: import('vue').Ref<boolean>, stop: () => void }}
 *          `visible`: si toca enseñar el esqueleto ahora mismo
 */
export function useDelayedLoading(source, { delay = SKELETON_DELAY_MS, minimum = SKELETON_MINIMUM_MS } = {}) {
  const read = typeof source === 'function' ? source : () => Boolean(unref(source))
  const visible = ref(false)

  let showTimer = null
  let hideTimer = null
  let shownAt = 0

  const clear = () => {
    clearTimeout(showTimer)
    clearTimeout(hideTimer)
    showTimer = null
    hideTimer = null
  }

  function onChange(loading) {
    if (loading) {
      // Vuelve a cargar mientras se estaba retirando: se queda.
      clearTimeout(hideTimer)
      hideTimer = null
      if (visible.value || showTimer) return
      showTimer = setTimeout(() => {
        showTimer = null
        visible.value = true
        shownAt = Date.now()
      }, delay)
      return
    }
    // Llegó antes del umbral: nunca se enseña.
    if (showTimer) {
      clearTimeout(showTimer)
      showTimer = null
      return
    }
    if (!visible.value || hideTimer) return
    const left = minimum - (Date.now() - shownAt)
    if (left <= 0) {
      visible.value = false
      return
    }
    hideTimer = setTimeout(() => {
      hideTimer = null
      visible.value = false
    }, left)
  }

  // `sync`: el temporizador arranca en el mismo instante en que cambia la
  // carga, no en el siguiente pintado; sin eso el umbral mide 150 ms más un
  // fotograma.
  const stopWatch = watch(read, onChange, { immediate: true, flush: 'sync' })

  const stop = () => { stopWatch(); clear() }
  if (getCurrentScope()) onScopeDispose(stop)

  return { visible, stop }
}
